import { select } from "d3";

import {
  AnimatedGrowthLabel,
  AppletElement,
  EdgeEndpoints,
  GROWTH_BACKGROUND_EDGE_STROKE,
  GROWTH_BACKGROUND_NODE_FILL,
  GROWTH_BACKGROUND_NODE_STROKE,
  GROWTH_BACKGROUND_OPACITY,
  GroupSelection,
  appendStepControls,
  renderAnimatedGrowthLabels,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type PathGrowthNode = Readonly<{
  id: string;
  x: number;
  y: number;
}>;

type PathGrowthEdge = Readonly<{
  source: string;
  target: string;
}>;

type PathGrowthLabelMap = Readonly<Record<string, number>>;

type PathGrowthState = Readonly<{
  step: number;
  labels: PathGrowthLabelMap;
  visibleNodeIds: readonly string[];
  visibleEdges: readonly PathGrowthEdge[];
  maxLabel: number;
  distinguishedNodeId: string;
}>;

const PATH_GROWTH_EDGE_COUNT = 8;
const PATH_GROWTH_WIDTH = 620;
const PATH_GROWTH_HEIGHT = 132;
const PATH_GROWTH_NODE_RADIUS = 12;
const PATH_GROWTH_NODE_FILL = "#8fbc8f";
const PATH_GROWTH_ACTIVE_FILL = "#dceaa8";
const PATH_GROWTH_NODE_STROKE = "#3f5f3f";
const PATH_GROWTH_EDGE_STROKE = "#444";
const PATH_GROWTH_LABEL_FILL = "#263726";

const PATH_GROWTH_NODES: readonly PathGrowthNode[] = Array.from(
  { length: PATH_GROWTH_EDGE_COUNT + 1 },
  (_unused: unknown, index: number): PathGrowthNode => ({
    id: `p${index}`,
    x: 54 + index * 64,
    y: index % 2 === 0 ? 78 : 54,
  }),
);

const PATH_GROWTH_STATES = buildPathGrowthStates();
const PATH_GROWTH_FINAL_STATE = PATH_GROWTH_STATES[PATH_GROWTH_STATES.length - 1];

function pathGrowthNodeLookup(): ReadonlyMap<string, PathGrowthNode> {
  return new Map(PATH_GROWTH_NODES.map((node: PathGrowthNode): [string, PathGrowthNode] => [node.id, node]));
}

function buildPathGrowthStates(): readonly PathGrowthState[] {
  let labels: Record<string, number> = { p0: 0 };
  let visibleNodeIds: readonly string[] = ["p0"];
  let visibleEdges: readonly PathGrowthEdge[] = [];
  let distinguishedNodeId = "p0";
  const states: PathGrowthState[] = [{
    step: 0,
    labels: { ...labels },
    visibleNodeIds: [...visibleNodeIds],
    visibleEdges: [...visibleEdges],
    maxLabel: 0,
    distinguishedNodeId,
  }];

  for (let step = 1; step <= PATH_GROWTH_EDGE_COUNT; step += 1) {
    const q = visibleEdges.length;
    const targetNodeId = `p${step}`;
    const distinguishedLabel = labels[distinguishedNodeId];
    const nextLabels: Record<string, number> = {};

    if (distinguishedLabel === 0) {
      Object.entries(labels).forEach(([nodeId, label]: [string, number]): void => {
        nextLabels[nodeId] = label;
      });
      nextLabels[targetNodeId] = q + 1;
    } else {
      Object.entries(labels).forEach(([nodeId, label]: [string, number]): void => {
        nextLabels[nodeId] = label + 1;
      });
      nextLabels[targetNodeId] = 0;
    }

    labels = nextLabels;
    visibleNodeIds = [...visibleNodeIds, targetNodeId];
    visibleEdges = [...visibleEdges, { source: distinguishedNodeId, target: targetNodeId }];
    distinguishedNodeId = targetNodeId;
    states.push({
      step,
      labels: { ...labels },
      visibleNodeIds: [...visibleNodeIds],
      visibleEdges: [...visibleEdges],
      maxLabel: visibleEdges.length,
      distinguishedNodeId,
    });
  }

  return states;
}

function pathGrowthNodes(state: PathGrowthState): readonly PathGrowthNode[] {
  const nodes = pathGrowthNodeLookup();
  return state.visibleNodeIds.reduce<PathGrowthNode[]>((visibleNodes, nodeId: string): PathGrowthNode[] => {
    const node = nodes.get(nodeId);
    if (node !== undefined) {
      visibleNodes.push(node);
    }
    return visibleNodes;
  }, []);
}

function pathGrowthEdgeEndpoints(edge: PathGrowthEdge, nodes: ReadonlyMap<string, PathGrowthNode>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, PATH_GROWTH_NODE_RADIUS);
}

function pathGrowthNodeFill(state: PathGrowthState, node: PathGrowthNode): string {
  const label = state.labels[node.id];
  if (label === 0 || label === state.maxLabel) {
    return PATH_GROWTH_ACTIVE_FILL;
  }

  return PATH_GROWTH_NODE_FILL;
}

function pathGrowthLabels(state: PathGrowthState): readonly AnimatedGrowthLabel[] {
  return pathGrowthNodes(state).map((node: PathGrowthNode): AnimatedGrowthLabel => ({
    id: node.id,
    label: state.labels[node.id].toString(),
    x: node.x,
    y: node.y + 0.5,
    fontSize: 11,
    fill: PATH_GROWTH_LABEL_FILL,
  }));
}

function renderPathGrowthBackground(graph: GroupSelection): void {
  const nodes = pathGrowthNodeLookup();
  const background = graph.append<SVGGElement>("g")
    .attr("class", "path-growth-background")
    .attr("opacity", GROWTH_BACKGROUND_OPACITY)
    .attr("pointer-events", "none");

  background.append<SVGGElement>("g")
    .attr("class", "path-growth-background-edges")
    .selectAll<SVGLineElement, PathGrowthEdge>("line")
    .data(PATH_GROWTH_FINAL_STATE.visibleEdges)
    .join("line")
    .attr("x1", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).y2)
    .attr("stroke", GROWTH_BACKGROUND_EDGE_STROKE)
    .attr("stroke-width", 1.1)
    .attr("stroke-linecap", "round");

  background.append<SVGGElement>("g")
    .attr("class", "path-growth-background-nodes")
    .selectAll<SVGCircleElement, PathGrowthNode>("circle")
    .data(PATH_GROWTH_NODES)
    .join("circle")
    .attr("cx", (node: PathGrowthNode): number => node.x)
    .attr("cy", (node: PathGrowthNode): number => node.y)
    .attr("r", PATH_GROWTH_NODE_RADIUS)
    .attr("fill", GROWTH_BACKGROUND_NODE_FILL)
    .attr("stroke", GROWTH_BACKGROUND_NODE_STROKE)
    .attr("stroke-width", 1.2);
}

function renderPathGrowthFrame(
  graph: GroupSelection,
  state: PathGrowthState,
  element: AppletElement,
  previousState?: PathGrowthState,
): void {
  element.dataset.pathStep = state.step.toString();
  element.dataset.pathMax = state.maxLabel.toString();
  graph.selectAll("*").remove();

  const nodes = pathGrowthNodeLookup();
  const visibleNodes = pathGrowthNodes(state);

  renderPathGrowthBackground(graph);

  graph.append<SVGGElement>("g")
    .attr("class", "path-growth-edges")
    .selectAll<SVGLineElement, PathGrowthEdge>("line")
    .data(state.visibleEdges)
    .join("line")
    .attr("x1", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: PathGrowthEdge): number => pathGrowthEdgeEndpoints(edge, nodes).y2)
    .attr("stroke", PATH_GROWTH_EDGE_STROKE)
    .attr("stroke-width", 1.4);

  graph.append<SVGGElement>("g")
    .attr("class", "path-growth-nodes")
    .selectAll<SVGCircleElement, PathGrowthNode>("circle")
    .data(visibleNodes)
    .join("circle")
    .attr("cx", (node: PathGrowthNode): number => node.x)
    .attr("cy", (node: PathGrowthNode): number => node.y)
    .attr("r", PATH_GROWTH_NODE_RADIUS)
    .attr("fill", (node: PathGrowthNode): string => pathGrowthNodeFill(state, node))
    .attr("stroke", PATH_GROWTH_NODE_STROKE)
    .attr("stroke-width", 2);

  renderAnimatedGrowthLabels(
    graph,
    "path-growth-labels",
    pathGrowthLabels(state),
    previousState === undefined ? undefined : pathGrowthLabels(previousState),
  );
}

function renderPathGrowth(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const svg = select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${PATH_GROWTH_WIDTH} ${PATH_GROWTH_HEIGHT}`)
    .attr("width", PATH_GROWTH_WIDTH)
    .attr("height", PATH_GROWTH_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "Animated construction of a gracefully labeled path by extreme sprout moves.");

  const graph = svg.append<SVGGElement>("g")
    .attr("class", "path-growth-graph");
  let previousState: PathGrowthState | undefined;
  let previousFrameIndex: number | undefined;

  appendStepControls({
    element,
    frameCount: PATH_GROWTH_STATES.length,
    onFrameChange: (frameIndex: number): void => {
      const state = PATH_GROWTH_STATES[frameIndex];
      const shouldAnimateLabels = previousFrameIndex !== undefined && frameIndex > previousFrameIndex;
      renderPathGrowthFrame(graph, state, element, shouldAnimateLabels ? previousState : undefined);
      previousState = state;
      previousFrameIndex = frameIndex;
    },
  });
}

renderApplet(".path-growth-applet", renderPathGrowth);
